import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideZonelessChangeDetection,
  Provider,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';

import { provideStore, withNgxsNoopExecutionStrategy } from '@ngxs/store';
import { environment } from 'src/environments/environment';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { AuthState } from './core/states/auth.state';
import { CoreModule } from './core/core.module';
import { HTTP_INTERCEPTORS, HttpErrorResponse, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { OrganisationsState } from './core/states/organisations.state';
import { UsersState } from './core/states/users.state';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { UploadsState } from './core/states/uploads.state';
import { IMAGE_CONFIG, IMAGE_LOADER, ImageLoaderConfig, provideImgixLoader, registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { SusbcriptionTypesState } from './core/states/subscription-types.state';
import { RequestCountInterceptor } from './core/interceptors/request-count.interceptor';
import { GenericErrorHandler } from './core/error-handlers/generic-error.handler';
import { AttachmentState } from './core/states/attachments.state';
import { SelectionState } from './core/states/selection.state';
import { KlicMetadataState } from './core/states/klic-metadata';
import { AppState } from './core/states/app.state';
import { ProjectsState } from './core/states/project.state';
import { ShopState } from './core/states/shop.state';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MapState } from './core/states/map.state';
import { StateNames } from './core/states/models/state-names.enum';
import {
  NavigationError,
  provideRouter,
  RedirectCommand,
  Router,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';

registerLocaleData(localeNl, 'nl');

const providers: (EnvironmentProviders | Provider)[] = [
  provideClientHydration(withEventReplay()),
  provideRouter(
    routes,
    withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    // Render the not-found page for resolver 404s while keeping the requested URL in the address bar.
    withNavigationErrorHandler(({ error, url }: NavigationError) => {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return new RedirectCommand(inject(Router).parseUrl('/pagina-niet-gevonden'), { browserUrl: url });
      }
      return undefined;
    })
  ),
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  provideAnimationsAsync(),
  provideStore(
    [
      AuthState,
      OrganisationsState,
      UsersState,
      UploadsState,
      SusbcriptionTypesState,
      AttachmentState,
      SelectionState,
      KlicMetadataState,
      AppState,
      ProjectsState,
      ShopState,
      ProjectsState,
      MapState,
    ],
    {
      selectorOptions: {
        injectContainerState: false,
        suppressErrors: false,
      },
      developmentMode: !environment.production,
    },
    withNgxsNoopExecutionStrategy(),
    withNgxsReduxDevtoolsPlugin({
      disabled: environment.production,
    }),
    withNgxsStoragePlugin({
      keys: [StateNames.shop_v3, StateNames.auth_v3, StateNames.app_v4, StateNames.map_v6],
      migrations: [],
    })
  ),
  importProvidersFrom(
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    })
  ),
  importProvidersFrom(CoreModule),
  provideImgixLoader('https://geogap.imgix.net/'),
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: RequestCountInterceptor, multi: true },
  { provide: LOCALE_ID, useValue: 'nl-NL' },
  { provide: ErrorHandler, useClass: GenericErrorHandler },
  provideNativeDateAdapter(),
  provideZonelessChangeDetection(),
  provideBrowserGlobalErrorListeners(),
];

if (environment.useImgix === true) {
  providers.push(provideImgixLoader(environment.imgixDomain), {
    provide: IMAGE_CONFIG,
    useValue: {
      placeholderResolution: 30,
    },
  });
} else {
  providers.push({
    provide: IMAGE_LOADER,
    useValue: (cfg: ImageLoaderConfig) => {
      return cfg.src;
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers,
};
